import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { Paper, ButtonGroup, Button } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { frFR, enUS } from '@mui/x-data-grid/locales';
import { formatDateTime, showDeleteResults } from '@utils/helpers';
import API from '@/utils/axios';
import theme from '@/theme';
import { useAuth } from '@contexts/AuthContext';
import { useSnackbar } from '@contexts/SnackbarContext';
import { useDialog } from '@contexts/DialogContext';
import useLocalStorage from '@/hooks/useLocalStorage';
import OurPagination from '@components/OurPagination';
import OurDataToolbar from '@components/OurDataToolbar';
import MailingListSearch from '@components/mailinglist/MailingListSearch';

function MailingListTable({ reloadTable }) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { doLogout, isSuperAdmin } = useAuth();
    const { showMessage } = useSnackbar();
    const { requestConfirm } = useDialog();
    const [loading, setLoading] = useState(true);
    const [mailinglists, setMailingLists] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [paginationModel, setPaginationModel] = useLocalStorage('mailinglists.pagination', {
        pageSize: 10,
        page: 0,
    });
    const [sortModel, setSortModel] = useLocalStorage('mailinglists.sort', [{
        field: 'id',
        sort: 'desc',
    }]);
    const [columnVisibility, setColumnVisibility] = useLocalStorage('mailinglists.columns', {
        updated_at: false,
    });
    const [density, setDensity] = useLocalStorage('mailinglists.density', 'standard');
    const [searchVisible, setSearchVisible] = useLocalStorage('mailinglists.searchVisible', false);
    const [filter, setFilter] = useLocalStorage('mailinglists.searchValues', {});
    const [selectedRows, setSelectedRows] = useState([]);

    const openEditPage = (params) => {
        navigate(`/mailinglists/${params.id}`);
    };

    const toggleSearch = () => {
        if (searchVisible) {
            setFilter({});
        }
        setSearchVisible(!searchVisible);
    };

    const handleDelete = async () => {
        requestConfirm({
            message: t('Do you really want to delete the selected mailinglists?'), 
            onConfirm: async () => {
                try {
                    const results = await API.delete(`/mailing-lists/batch`, { data: { ids: selectedRows } });
                    showDeleteResults(results, showMessage, t, '{{nb}} mailinglist deleted successfully', '{{nb}} mailinglists deleted successfully');
                    loadMailingLists(paginationModel, sortModel); // Refresh
                } catch (error) {
                    if (error.response && error.response.status === 401) {
                        doLogout();
                    } else {
                        showMessage(t(error.message), 'error');
                    }
                }
            }
        });
    };

    const loadMailingLists = useCallback(async (paginationModel, sortModel) => {
        try {
            setLoading(true);
            const { field = '', sort = '' } = sortModel[0] || {};
            let params = {
                per_page: paginationModel.pageSize,
                page: paginationModel.page + 1,
                sort: field,
                direction: sort,
            };
            const response = await API.get('/mailing-lists', { params: { ...params, ...filter } });
            setMailingLists(
                response.data.data.map((mailinglist) => ({
                    id: mailinglist.id,
                    name: mailinglist.name,
                    active: mailinglist.active,
                    customer_id: mailinglist.customer_id,
                    customer_name: mailinglist.customer_name,
                    created_at: new Date(mailinglist.created_at),
                    updated_at: new Date(mailinglist.updated_at),
                    contact_count: mailinglist.contact_count,
                }))
            );
            setRowCount(response.data.total);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            if (error.response && error.response.status === 401) {
                doLogout();
            } else {
                console.error('Erreur de chargement', error);
            }
        }
    }, [doLogout, filter]);

    useEffect(() => {
        loadMailingLists(paginationModel, sortModel);
    }, [loadMailingLists, paginationModel, sortModel, reloadTable]);

    const localeTheme = i18n.language === 'fr' ? frFR : enUS;
    const themeWithLocale = createTheme(theme, localeTheme);

    const columns = [
        {
            field: 'id',
            headerName: t('ID'),
            flex: 0.5,
            type: 'number',
            maxWidth: 70,
        },
        {
            field: 'name',
            headerName: t('Name'),
            flex: 1,
            type: 'string'
        },
        ...(isSuperAdmin() ? [
            {
                field: 'customer_id',
                headerName: t('Customer ID'),
                flex: 1,
                type: 'number'
            },
            {
                field: 'customer_name',
                headerName: t('Customer'),
                flex: 1,
                type: 'string'
            },
        ] : []),
        {
            field: 'created_at',
            headerName: t('Creation date'),
            flex: 1,
            type: 'dateTime',
            valueFormatter: formatDateTime
        },
        {
            field: 'updated_at',
            headerName: t('Update date'),
            flex: 1,
            type: 'dateTime',
            valueFormatter: formatDateTime
        },
        {
            field: 'contact_count',
            headerName: t('Contacts'),
            flex: 1,
            type: 'number',
            maxWidth: 100,
        },
    ];

    return (
        <ThemeProvider theme={themeWithLocale}>
            <Paper>
                {searchVisible && <MailingListSearch handleSubmit={setFilter} filter={filter} />}
                <DataGrid
                    autoHeight
                    loading={loading}
                    slots={{
                        toolbar: OurDataToolbar,
                        pagination: OurPagination,
                    }}
                    slotProps={{
                        toolbar: { searchVisible, toggleSearch },
                    }}
                    initialState={{
                        sorting: { sortModel }
                    }}
                    rows={mailinglists}
                    columns={columns}
                    pageSizeOptions={[10, 15, 20, 50, 100]}
                    pagination
                    paginationMode="server"
                    rowCount={rowCount}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    columnVisibilityModel={columnVisibility}
                    onColumnVisibilityModelChange={(newModel) =>
                        setColumnVisibility(newModel)
                    }
                    sortingMode="server"
                    sortingOrder={['asc', 'desc']}
                    onSortModelChange={setSortModel}
                    density={density}
                    onDensityChange={setDensity}
                    disableRowSelectionOnClick
                    disableSelectionOnClick
                    disableColumnResize={true}
                    disableColumnFilter
                    checkboxSelection
                    onRowClick={openEditPage}
                    onRowSelectionModelChange={(newSelectionModel) => {
                        setSelectedRows(newSelectionModel);
                    }}
                />

            </Paper>

            <ButtonGroup variant="contained" aria-label="Actions" sx={{ mt: 2 }}>
                <Button onClick={handleDelete} startIcon={<DeleteIcon />}
                    disabled={selectedRows.length === 0}>
                    {t('Delete')}
                </Button>
            </ButtonGroup>
        </ThemeProvider>
    );
}

export default MailingListTable;
